import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import { deleteSite, getSites } from '../../../../../api/endpoints/cmsSite';
import type { Site } from '../../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../../components/ListSearchField';
import { SiteFormDialog } from '../SiteFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/cmsSite.ts header). Site is the root of the CMS hierarchy — unlike its 11 child
 *  resources (Header/SiteTitle/Slider/Contact/About/Contract/City/PopulerCity/
 *  CheapestDestination/Destination/Campaign), which no longer have their own list pages/menu
 *  items, it has no parent-id filter. Selecting "Yönet" on a row navigates to
 *  `SiteDetailPage`, which renders all 11 child resources as tabs scoped to that one site. */
export default function SiteListPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);
  const [deleting, setDeleting] = useState<Site | null>(null);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({ queryKey: ['cms', 'sites'], queryFn: () => getSites() });
  const rows: Site[] = useMemo(() => query.data?.sites ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.company, row.address, row.tursab, row.iata]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteSite(id),
    onSuccess: () => {
      enqueueSnackbar('Site silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'sites'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Site Ayarları</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Site
        </Button>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Firma, adres, TURSAB, IATA..." />
      </Box>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Firma</TableCell>
              <TableCell>Adres</TableCell>
              <TableCell>TURSAB</TableCell>
              <TableCell>IATA</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı site bulunamadı.' : 'Aramayla eşleşen site bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.company ?? '—'}</TableCell>
                <TableCell>{row.address ?? '—'}</TableCell>
                <TableCell>{row.tursab ?? '—'}</TableCell>
                <TableCell>{row.iata ?? '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Yönet (Header, Slider, İletişim, Şehirler, Kampanyalar…)">
                    <IconButton size="small" onClick={() => navigate(`/cms/site/${row.id}`)} aria-label="Yönet">
                      <TuneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SiteFormDialog open={createOpen} site={null} onClose={() => setCreateOpen(false)} />
      <SiteFormDialog open={!!editing} site={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Siteyi sil"
        description={`"${deleting?.company ?? ''}" sitesini silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
